<template>
  <span>{{ content }}</span>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import isValid from 'date-fns/isValid';
import lightFormat from 'date-fns/lightFormat';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import parseDate from 'date-fns/parse';
import differenceInSeconds from 'date-fns/differenceInSeconds';
import parseISO from 'date-fns/parseISO';

@Component
export default class FormatTime extends Vue {
  /**
   * The date to show. This can be a UNIX timestamp, JS Date or a ISO date string. If your date is in a custom
   * format, you can use this along with the `parse` prop to parse specific date formats.
   */
  @Prop([String, Date, Number]) readonly date!: number | Date | string;
  /**
   * The format to show the date in, uses `date-fns` tokens. you can check
   * the official tokens from [Date FNS Tokens](https://date-fns.org/v2.29.1/docs/lightFormat).
   *
   * This prop also supports a special value `"time-ago"`, which if specified, will show the
   * time elapsed from the `date` till now.
   */
  @Prop({ type: String, default: 'YYYY-MM-DD' }) readonly format!: string;
  /**
   * If your date comes in a string, you can use this prop to specify the format of the date.
   */
  @Prop({ type: [Boolean, String], default: true }) readonly parse!: boolean | string;
  /**
   * If enabled, then displays the time since `Date.now()` that has elapsed, until `until` seconds are
   * crossed. This will emulate a heartbeat in the component that updates every `frequency` milliseconds.
   *
   * Once the threshold is crossed, then, it will revert to show the `format` string.
   */
  @Prop(Boolean) readonly timeAgo!: boolean;
  /**
   * Optionally, display this text, iff the date is empty or invalid
   */
  @Prop({ default: '' }) readonly empty!: string;
  /**
   * If `timeAgo` is specified, this acts as a threshold to stop the heart beat started from `timeAgo` (in seconds).
   */
  @Prop({ type: Number, default: 300 }) readonly until!: number;
  /**
   * The refresh rate for the component (in milliseconds).
   */
  @Prop({ type: Number, default: 5000 }) readonly frequency!: number;
  /**
   * If specified, appends a suffix to the time, in `timeAgo` mode.
   */
  @Prop({ default: 'ago' }) readonly timeAgoSuffix!: string;

  interval!: ReturnType<typeof setInterval>;
  /**
   * Random number to force the component to refresh.
   */
  heartBeats = 1;

  get timeAgoThresholdCrossed() {
    const { parsedDate, until: secondsThreshold } = this;
    if (!parsedDate) return true;
    return differenceInSeconds(Date.now(), parsedDate) > secondsThreshold;
  }

  get parsedDate() {
    const { date, parse } = this;

    if (typeof date === 'string') {
      if (typeof parse === 'boolean') {
        return parse ? isValid(parseISO(date)) && parseISO(date) : isValid(new Date(date)) && new Date(date);
      } else if (parse) {
        const parsedDate = parseDate(date, parse, Date.now());
        return isValid(parsedDate) && parsedDate;
      }
      const parsedDate = new Date(date);
      return parsedDate;
    }

    return date && isValid(date) && date;
  }

  get content() {
    const { timeAgo, parsedDate, empty: emptyMsg, format, heartBeats } = this;

    if (!parsedDate) return emptyMsg;

    if (heartBeats && (timeAgo || format === 'time-ago')) {
      return formatDistanceToNow(parsedDate, { addSuffix: true });
    }

    return lightFormat(parsedDate, format);
  }

  created() {
    this.setupInterval();
  }

  beforeDestroy() {
    this.killHeartbeat();
  }

  @Watch('timeAgo')
  @Watch('until')
  @Watch('frequency')
  setupInterval() {
    const { frequency: timeAgoFrequency, timeAgo, interval: currentIntervalId, timeAgoThresholdCrossed } = this;
    this.killHeartbeat(currentIntervalId);

    if (timeAgo && !timeAgoThresholdCrossed) {
      // Start heartbeat.
      const interval = setInterval(() => {
        this.heartBeats = (this.heartBeats % 10) + 1;
        if (this.timeAgoThresholdCrossed) this.killHeartbeat(interval);
      }, timeAgoFrequency);

      this.interval = interval;
    }
  }

  killHeartbeat(interval: null | ReturnType<typeof setInterval> = null) {
    interval = interval || this.interval;
    clearInterval(interval);
    this.heartBeats++;
  }
}
</script>
